import {
  Add as AddIcon,
  AllInbox as AllInboxIcon,
  ContactMail,
  DeleteOutline,
  Domain as DomainIcon,
  DraftsOutlined,
  Drafts,
  Edit,
  Email,
  Error as ErrorIcon,
  FileUpload,
  Inbox,
  MoreVert,
  Psychology,
  RefreshOutlined,
  Reply,
  Search as SearchIcon,
  Send,
  SyncDisabled,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
  LocalFireDepartment,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  DEMO_MODE_KEY,
  INBOX_MESSAGE_TYPE_BOUNCED,
  INBOX_MESSAGE_TYPE_DRAFT_COMPOSE,
  INBOX_MESSAGE_TYPE_DRAFT_REPLY,
  INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE,
  INBOX_MESSAGE_TYPE_RECEIVED,
  INBOX_MESSAGE_TYPE_RECEIVED_REPLY,
  INBOX_MESSAGE_TYPE_SENT_COMPOSE,
  INBOX_MESSAGE_TYPE_SENT_REPLY,
  INBOX_MESSAGE_TYPE_SENT_SEQUENCE,
  INBOX_MESSAGE_TYPE_WARMUP,
  INBOX_TYPE_GMAIL,
  INBOX_TYPE_OFFICE365,
  INBOX_TYPE_DOMAIN,
} from "../../constants";
import { DEMO_INBOXES, DEMO_INBOX_MESSAGES } from "../../constants/demoData";
import { useWhitelabel } from "../../context/WhitelabelContext";
import googleLogo from "../../img/google-logo.png";
import office365Logo from "../../img/office-365-logo.png";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import InstallIntegrationDialog from "../integrations/InstallIntegrationDialog";
import InboxComposeDialog from "./InboxComposeDialog";
import InboxDomainDialog from "./InboxDomainDialog";
import InboxEditorDialog from "./InboxEditorDialog";
import InboxMessagePreviewDialog from "./InboxMessagePreviewDialog";
import InboxReplyDialog from "./InboxReplyDialog";
import InboxTypeSelectionDialog from "./InboxTypeSelectionDialog";
import InboxTransferDialog from "./dialogs/InboxTransferDialog";
import InboxWarmupDialog from "./dialogs/InboxWarmupDialog";
import InboxImportDialog from "./dialogs/InboxImportDialog";
import ColdOutreachOrderDialog from "./dialogs/ColdOutreachOrderDialog";
import SenderProfiles from "../sequences/SenderProfiles";
import InboxMetricsBar from "./InboxMetricsBar";

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  employees,
}: {
  getToken: () => Promise<string>;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  eventListener: (event: any) => void;
  currentUser: any | User;
  companyId: string;
  company: Company;
  employees: Array<any>;
}) => {
  const { whitelabelSettings } = useWhitelabel();
  const [inboxes, setInboxes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedInbox, setSelectedInbox] = useState<any>(undefined); // undefined = not initialized, null = "All Inboxes"
  const [editingInbox, setEditingInbox] = useState<any>(null); // Separate state for the inbox being edited
  const [domainOpen, setDomainOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Messages state
  const [messages, setMessages] = useState<Array<any>>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesRefreshing, setMessagesRefreshing] = useState(false);
  const [messagesPage, setMessagesPage] = useState(0);
  const [messagesPageSize, setMessagesPageSize] = useState(50);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState<any>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [messageMenuAnchor, setMessageMenuAnchor] =
    useState<null | HTMLElement>(null);

  // Inbox type selection state
  const [typeSelectionOpen, setTypeSelectionOpen] = useState(false);
  const [imapDialogOpen, setImapDialogOpen] = useState(false);
  const [office365DialogOpen, setOffice365DialogOpen] = useState(false);
  const [gmailDialogOpen, setGmailDialogOpen] = useState(false);
  const [messageMenuId, setMessageMenuId] = useState<string | null>(null);

  // Transfer dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [inboxToTransfer, setInboxToTransfer] = useState<any>(null);

  // Warmup dialog state
  const [warmupDialogOpen, setWarmupDialogOpen] = useState(false);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Cold outreach order dialog state
  const [coldOutreachDialogOpen, setColdOutreachDialogOpen] = useState(false);


  // Sender profiles dialog state
  const [senderProfilesOpen, setSenderProfilesOpen] = useState(false);

  // Mobile more options menu state
  const [moreOptionsAnchor, setMoreOptionsAnchor] = useState<null | HTMLElement>(null);

  // Message type filtering
  const [selectedMessageTypes, setSelectedMessageTypes] = useState<string[]>([
    "received",
    "received_reply",
  ]);

  // Search state (for messages)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Inbox search state (for left panel)
  const [inboxSearchQuery, setInboxSearchQuery] = useState("");
  const [debouncedInboxSearchQuery, setDebouncedInboxSearchQuery] = useState("");

  // Demo mode state
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  // Listen for demo mode changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };

    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };

    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Group inboxes by domain for mobile selector
  const inboxesByDomain = useMemo(() => {
    const groups: Record<string, any[]> = {};
    inboxes.forEach((inbox: any) => {
      // Extract domain from inbox.domain field
      // For IMAP/Office365 inboxes, domain field contains full email address
      // For domain inboxes, it contains just the domain
      let domain = inbox.domain || "No Domain";
      if (domain.includes("@")) {
        domain = domain.split("@")[1];
      }
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(inbox);
    });
    // Sort domains alphabetically
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([domain, domainInboxes]) => ({
        domain,
        inboxes: domainInboxes.sort((a: any, b: any) =>
          (a.name || "").localeCompare(b.name || "")
        ),
      }));
  }, [inboxes]);

  // All available message types (grouped)
  const ALL_MESSAGE_TYPES = [
    {
      label: "Inbox",
      icon: <Inbox fontSize="small" />,
      types: [INBOX_MESSAGE_TYPE_RECEIVED],
    },
    {
      label: "Inbox Reply",
      icon: <Inbox fontSize="small" />,
      types: [INBOX_MESSAGE_TYPE_RECEIVED_REPLY],
    },
    {
      label: "Sent",
      icon: <Send fontSize="small" />,
      types: [
        INBOX_MESSAGE_TYPE_SENT_COMPOSE,
        INBOX_MESSAGE_TYPE_SENT_REPLY,
        INBOX_MESSAGE_TYPE_SENT_SEQUENCE,
      ],
    },
    {
      label: "Draft",
      icon: <Drafts fontSize="small" />,
      types: [
        INBOX_MESSAGE_TYPE_DRAFT_COMPOSE,
        INBOX_MESSAGE_TYPE_DRAFT_REPLY,
        INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE,
      ],
    },
    {
      label: "Warmup",
      icon: <LocalFireDepartment fontSize="small" />,
      types: [INBOX_MESSAGE_TYPE_WARMUP],
    },
    {
      label: "Bounced",
      icon: <ErrorIcon fontSize="small" />,
      types: [INBOX_MESSAGE_TYPE_BOUNCED],
    },
  ];

  // Helper functions for reply classification
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "interested":
        return "success";
      case "not_interested":
        return "error";
      case "meeting_request":
        return "primary";
      case "out_of_office":
        return "warning";
      case "question":
        return "info";
      case "positive":
        return "success";
      case "negative":
        return "error";
      case "unsubscribe":
        return "error";
      case "bounce":
        return "error";
      case "complaint":
        return "error";
      case "referral":
        return "secondary";
      case "auto_reply":
        return "default";
      default:
        return "default";
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <TrendingUp fontSize="small" />;
      case "negative":
        return <TrendingDown fontSize="small" />;
      case "neutral":
        return <TrendingFlat fontSize="small" />;
      default:
        return null;
    }
  };

  const formatCategory = (category: string) => {
    return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getMessageTypeIcon = (messageType: string) => {
    switch (messageType) {
      case INBOX_MESSAGE_TYPE_RECEIVED:
      case INBOX_MESSAGE_TYPE_RECEIVED_REPLY:
        return <Inbox fontSize="small" />;
      case INBOX_MESSAGE_TYPE_SENT_COMPOSE:
      case INBOX_MESSAGE_TYPE_SENT_REPLY:
      case INBOX_MESSAGE_TYPE_SENT_SEQUENCE:
        return <Send fontSize="small" />;
      case INBOX_MESSAGE_TYPE_DRAFT_COMPOSE:
      case INBOX_MESSAGE_TYPE_DRAFT_REPLY:
      case INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE:
        return <Drafts fontSize="small" />;
      case INBOX_MESSAGE_TYPE_WARMUP:
        return <LocalFireDepartment fontSize="small" />;
      case INBOX_MESSAGE_TYPE_BOUNCED:
        return <ErrorIcon fontSize="small" />;
      default:
        return <Inbox fontSize="small" />;
    }
  };

  const getMessageTypeColor = (messageType: string) => {
    switch (messageType) {
      case INBOX_MESSAGE_TYPE_RECEIVED:
      case INBOX_MESSAGE_TYPE_RECEIVED_REPLY:
        return "primary";
      case INBOX_MESSAGE_TYPE_SENT_COMPOSE:
      case INBOX_MESSAGE_TYPE_SENT_REPLY:
      case INBOX_MESSAGE_TYPE_SENT_SEQUENCE:
        return "success";
      case INBOX_MESSAGE_TYPE_DRAFT_COMPOSE:
      case INBOX_MESSAGE_TYPE_DRAFT_REPLY:
      case INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE:
        return "warning";
      case INBOX_MESSAGE_TYPE_WARMUP:
        return "error";
      case INBOX_MESSAGE_TYPE_BOUNCED:
        return "error";
      default:
        return "default";
    }
  };

  const getMessageTypeLabel = (messageType: string) => {
    switch (messageType) {
      case INBOX_MESSAGE_TYPE_RECEIVED:
        return "Inbox";
      case INBOX_MESSAGE_TYPE_RECEIVED_REPLY:
        return "Reply";
      case INBOX_MESSAGE_TYPE_SENT_COMPOSE:
      case INBOX_MESSAGE_TYPE_SENT_REPLY:
      case INBOX_MESSAGE_TYPE_SENT_SEQUENCE:
        return "Sent";
      case INBOX_MESSAGE_TYPE_DRAFT_COMPOSE:
      case INBOX_MESSAGE_TYPE_DRAFT_REPLY:
      case INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE:
        return "Draft";
      case INBOX_MESSAGE_TYPE_WARMUP:
        return "Warmup";
      case INBOX_MESSAGE_TYPE_BOUNCED:
        return "Bounced";
      default:
        return "Unknown";
    }
  };

  // Get inbox type icon (Gmail, Office365, Domain - skip IMAP)
  const getInboxTypeIcon = (inboxType: string) => {
    switch (inboxType) {
      case INBOX_TYPE_GMAIL:
        return (
          <Box
            component="img"
            src={googleLogo}
            alt="Gmail"
            sx={{ width: 16, height: 16 }}
          />
        );
      case INBOX_TYPE_OFFICE365:
        return (
          <Box
            component="img"
            src={office365Logo}
            alt="Office 365"
            sx={{ width: 16, height: 16 }}
          />
        );
      case INBOX_TYPE_DOMAIN:
        return <DomainIcon sx={{ fontSize: 16, color: "text.secondary" }} />;
      default:
        return <Email sx={{ fontSize: 16, color: "text.secondary" }} />;
    }
  };

  const getInboxTypeLabel = (inboxType: string) => {
    switch (inboxType) {
      case INBOX_TYPE_GMAIL:
        return "Gmail";
      case INBOX_TYPE_OFFICE365:
        return "Office 365";
      case INBOX_TYPE_DOMAIN:
        return "Domain";
      default:
        return "IMAP";
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Load more when user scrolls to within 100px of the bottom
    if (
      scrollHeight - scrollTop <= clientHeight + 100 &&
      hasMoreMessages &&
      !messagesLoading
    ) {
      loadMoreMessages();
    }
  };

  const deleteInbox = async (inboxId: string) => {
    const currentToken = await getToken();
    openDialog(
      "Delete Inbox",
      `Are you sure you want to delete this inbox?`,
      "Confirm",
      () => {
        setLoading(true);
        return axios
          .delete(`${hostname}/inboxes/${inboxId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            setLoading(false);
            refreshInboxes();
          });
      }
    );
  };

  const refreshInboxes = async (pageNum = page, searchQuery = debouncedInboxSearchQuery) => {
    // Use demo data if demo mode is enabled
    if (isDemoMode) {
      setRefreshing(true);
      setTimeout(() => {
        let filteredInboxes = DEMO_INBOXES;
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filteredInboxes = DEMO_INBOXES.filter(
            (inbox) =>
              inbox.name.toLowerCase().includes(query) ||
              inbox.email.toLowerCase().includes(query)
          );
        }
        setInboxes(filteredInboxes);
        setTotal(filteredInboxes.length);
        if (selectedInbox === undefined) {
          setSelectedInbox(null);
        }
        setRefreshing(false);
      }, 300);
      return;
    }

    setRefreshing(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/inboxes`, {
        params: {
          companyId,
          page: pageNum + 1,
          pageSize,
          query: searchQuery || undefined,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setInboxes(res.data.items);
        setTotal(res.data.total);
        // On first load, set selectedInbox to "All Inboxes" (null) to trigger message loading
        if (selectedInbox === undefined) {
          setSelectedInbox(null);
        }
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const handleInboxPageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    refreshInboxes(newPage);
  };

  const refreshMessages = async (reset = true) => {
    // Return early if no specific inbox is selected and we're not in "All Inboxes" mode
    if (selectedInbox === undefined) return;

    // Use demo data if demo mode is enabled
    if (isDemoMode) {
      setMessagesRefreshing(true);
      setTimeout(() => {
        let filteredMessages = DEMO_INBOX_MESSAGES;

        // Filter by inbox if specific inbox is selected
        if (selectedInbox !== null) {
          filteredMessages = filteredMessages.filter(
            (msg) => msg.inboxId === selectedInbox.id
          );
        }

        // Filter by message types
        if (selectedMessageTypes.length > 0) {
          filteredMessages = filteredMessages.filter((msg) =>
            selectedMessageTypes.includes(msg.messageType)
          );
        }

        // Filter by search query
        if (debouncedSearchQuery) {
          const query = debouncedSearchQuery.toLowerCase();
          filteredMessages = filteredMessages.filter(
            (msg) =>
              msg.subject.toLowerCase().includes(query) ||
              msg.bodyPreview.toLowerCase().includes(query) ||
              msg.sender.toLowerCase().includes(query) ||
              msg.senderName.toLowerCase().includes(query)
          );
        }

        setMessages(filteredMessages);
        setMessagesPage(1);
        setMessagesTotal(filteredMessages.length);
        setHasMoreMessages(false);
        setMessagesRefreshing(false);
      }, 300);
      return;
    }

    setMessagesRefreshing(true);
    const currentToken = await getToken();
    const pageToLoad = reset ? 1 : messagesPage + 1;

    // Determine the endpoint based on selection
    const endpoint =
      selectedInbox === null
        ? `${hostname}/inboxes/messages` // All inboxes
        : `${hostname}/inboxes/${selectedInbox.id}/messages`; // Specific inbox

    axios
      .get(endpoint, {
        params: {
          companyId,
          page: pageToLoad,
          pageSize: messagesPageSize,
          messageTypes: selectedMessageTypes.join(","),
          query: debouncedSearchQuery || undefined,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        if (reset) {
          setMessages(res.data.items);
          setMessagesPage(1);
        } else {
          setMessages((prev) => [...prev, ...res.data.items]);
          setMessagesPage(pageToLoad);
        }
        setMessagesTotal(res.data.total);
        setHasMoreMessages(res.data.has_next);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setMessagesRefreshing(false);
      });
  };

  const loadMoreMessages = async () => {
    if (selectedInbox === undefined || !hasMoreMessages || messagesLoading)
      return;

    setMessagesLoading(true);
    const currentToken = await getToken();

    // Determine the endpoint based on selection
    const endpoint =
      selectedInbox === null
        ? `${hostname}/inboxes/messages` // All inboxes
        : `${hostname}/inboxes/${selectedInbox.id}/messages`; // Specific inbox

    axios
      .get(endpoint, {
        params: {
          companyId,
          page: messagesPage + 1,
          pageSize: messagesPageSize,
          messageTypes: selectedMessageTypes.join(","),
          query: debouncedSearchQuery || undefined,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setMessages((prev) => [...prev, ...res.data.items]);
        setMessagesPage((prev) => prev + 1);
        setHasMoreMessages(res.data.has_next);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setMessagesLoading(false);
      });
  };

  const loadInboxes = async () => {
    setLoading(true);
    await refreshInboxes();
    setLoading(false);
  };

  const loadMessages = async () => {
    setMessagesLoading(true);
    await refreshMessages();
    setMessagesLoading(false);
  };

  const handleSelectInbox = (inbox: any) => {
    setSelectedInbox(inbox);
    setSelectedMessages([]);
    setMessages([]);
    setMessagesPage(0);
    setHasMoreMessages(true);
  };

  const handleSelectAllMessages = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const newSelected = messages.map((n) => n.id);
      setSelectedMessages(newSelected);
      return;
    }
    setSelectedMessages([]);
  };

  const handleSelectMessage = (id: string) => {
    const selectedIndex = selectedMessages.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedMessages, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedMessages.slice(1));
    } else if (selectedIndex === selectedMessages.length - 1) {
      newSelected = newSelected.concat(selectedMessages.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedMessages.slice(0, selectedIndex),
        selectedMessages.slice(selectedIndex + 1)
      );
    }

    setSelectedMessages(newSelected);
  };

  const handleBulkDeleteMessages = async () => {
    if (!selectedInbox) return;

    const currentToken = await getToken();
    try {
      await axios.post(
        `${hostname}/inboxes/${selectedInbox.id}/messages/delete`,
        { messageIds: selectedMessages },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      await refreshMessages();
      setSelectedMessages([]);
    } catch (error) {
      console.error("Error deleting messages:", error);
    }
  };

  const onViewMessage = (message: any) => {
    setSelectedMessage(message);
    setPreviewOpen(true);
  };

  const onReplyMessage = (message: any) => {
    setReplyMessage(message);
    setReplyOpen(true);
  };

  const onUnsubscribeSender = (email: string) => {
    if (!email) return;
    openDialog(
      "Add to unsubscribe list",
      `Add ${email} to the unsubscribe list? This blocks all future emails to this address from this company and removes them from any active sequences.`,
      "Add to unsubscribe list",
      async () => {
        try {
          const currentToken = await getToken();
          await axios.post(
            `${hostname}/unsubscribes`,
            { email, companyId, reason: "user_request" },
            { headers: { Authorization: `Bearer ${currentToken}` } }
          );
          eventListener({
            type: "SET_SUCCESS",
            message: `${email} added to the unsubscribe list.`,
          });
          setPreviewOpen(false);
          refreshMessages(true);
        } catch (err) {
          eventListener({ type: "SET_ERROR", error: parseError(err) });
        }
      }
    );
  };

  const isWebhookInbox = (id: string) => {
    const inboxToEdit: any = inboxes.find((i: any) => i.id === id);
    if (!inboxToEdit) {
      return false;
    }
    return inboxToEdit.inboxType === "webhook";
  };

  const isMessageSelected = (id: string) => selectedMessages.indexOf(id) !== -1;

  useEffect(() => {
    if (currentUser.uid) {
      refreshInboxes();
    }
  }, [page, isDemoMode]);

  useEffect(() => {
    if (currentUser.uid) {
      loadInboxes();
    }
  }, [currentUser.uid, companyId, isDemoMode]);

  useEffect(() => {
    if (selectedInbox !== undefined && currentUser.uid) {
      loadMessages();
    }
  }, [selectedInbox, companyId, isDemoMode]);

  // Debounce search query (for messages)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce inbox search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInboxSearchQuery(inboxSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [inboxSearchQuery]);

  // Refresh inboxes when debounced inbox search query changes
  useEffect(() => {
    if (companyId && currentUser.uid) {
      setPage(0);
      refreshInboxes(0, debouncedInboxSearchQuery);
    }
  }, [debouncedInboxSearchQuery]);

  // Refresh messages when message types filter changes
  useEffect(() => {
    if (selectedInbox !== undefined && companyId && currentUser.uid) {
      refreshMessages(true);
    }
  }, [selectedMessageTypes]);

  // Refresh messages when debounced search query changes
  useEffect(() => {
    if (selectedInbox !== undefined && companyId && currentUser.uid) {
      refreshMessages(true);
    }
  }, [debouncedSearchQuery]);

  return (
    <Box>
      <InboxMetricsBar companyId={companyId} getToken={getToken} />
      {/* Desktop buttons */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Button
          data-tutorial="add-inbox"
          variant="contained"
          onClick={() => setTypeSelectionOpen(true)}
          startIcon={<Inbox />}
        >
          Connect Mailboxes
        </Button>

        <Button
          variant="outlined"
          onClick={() => setColdOutreachDialogOpen(true)}
          startIcon={<AddIcon />}
        >
          Create Mailboxes
        </Button>

        <Button
          data-tutorial="warmup-settings"
          variant="outlined"
          onClick={() => setWarmupDialogOpen(true)}
          startIcon={<LocalFireDepartment />}
        >
          Inbox Warmup
        </Button>

        <Button
          variant="outlined"
          onClick={() => setSenderProfilesOpen(true)}
          startIcon={<ContactMail />}
        >
          Sender Profiles
        </Button>

        <Button
          variant="outlined"
          onClick={() => setImportDialogOpen(true)}
          startIcon={<FileUpload />}
        >
          Import
        </Button>
      </Box>

      {/* Mobile buttons - row 1 */}
      <Box sx={{ display: { xs: "block", sm: "none" }, mb: 1 }}>
        <Button
          variant="contained"
          onClick={() => setTypeSelectionOpen(true)}
          startIcon={<Inbox />}
          fullWidth
        >
          Connect Mailboxes
        </Button>
      </Box>

      {/* Mobile buttons - row 2 */}
      <Box sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={(e) => setMoreOptionsAnchor(e.currentTarget)}
          startIcon={<MoreVert />}
          fullWidth
        >
          More Options
        </Button>

        <Menu
          anchorEl={moreOptionsAnchor}
          open={Boolean(moreOptionsAnchor)}
          onClose={() => setMoreOptionsAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setColdOutreachDialogOpen(true);
              setMoreOptionsAnchor(null);
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Mailboxes</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setWarmupDialogOpen(true);
              setMoreOptionsAnchor(null);
            }}
          >
            <ListItemIcon>
              <LocalFireDepartment fontSize="small" />
            </ListItemIcon>
            <ListItemText>Inbox Warmup</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSenderProfilesOpen(true);
              setMoreOptionsAnchor(null);
            }}
          >
            <ListItemIcon>
              <ContactMail fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sender Profiles</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setImportDialogOpen(true);
              setMoreOptionsAnchor(null);
            }}
          >
            <ListItemIcon>
              <FileUpload fontSize="small" />
            </ListItemIcon>
            <ListItemText>Import</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Mobile Inbox Selector */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="mobile-inbox-select-label">Select Inbox</InputLabel>
          <Select
            labelId="mobile-inbox-select-label"
            value={selectedInbox?.id || "all"}
            label="Select Inbox"
            onChange={(e) => {
              const value = e.target.value;
              if (value === "all") {
                setSelectedInbox(null);
              } else {
                const inbox = inboxes.find((i: any) => i.id === value);
                if (inbox) {
                  setSelectedInbox(inbox);
                }
              }
            }}
          >
            <MenuItem value="all">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AllInboxIcon fontSize="small" />
                <Typography>All Inboxes</Typography>
                <Chip
                  label={total}
                  size="small"
                  sx={{ height: 20, fontSize: "0.75rem", ml: "auto" }}
                />
              </Box>
            </MenuItem>
            {inboxesByDomain.flatMap(({ domain, inboxes: domainInboxes }) => [
              <ListSubheader
                key={`header-${domain}`}
                sx={(theme) => ({
                  backgroundColor: theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
                  fontWeight: "bold",
                  lineHeight: "32px",
                })}
              >
                @{domain} ({domainInboxes.length})
              </ListSubheader>,
              ...domainInboxes.map((inbox: any) => (
                <MenuItem key={inbox.id} value={inbox.id} sx={{ pl: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {inbox.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Chip
                        label={inbox.sharing === "Shared" ? "Shared" : "Private"}
                        size="small"
                        color={inbox.sharing === "Shared" ? "primary" : "default"}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                      {getInboxTypeIcon(inbox.inboxType) && (
                        <Chip
                          icon={getInboxTypeIcon(inbox.inboxType) as React.ReactElement}
                          label={getInboxTypeLabel(inbox.inboxType)}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            "& .MuiChip-icon": {
                              marginLeft: "4px",
                              marginRight: "-4px",
                            },
                          }}
                        />
                      )}
                      {inbox.syncEnabled === false && (
                        <Chip
                          icon={<SyncDisabled sx={{ fontSize: 12 }} />}
                          label="No Sync"
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            "& .MuiChip-icon": {
                              marginLeft: "4px",
                              marginRight: "-4px",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 210px)",
          gap: 2,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Inboxes List - Desktop Only */}
        <Paper
          sx={{
            width: 300,
            minWidth: 300,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
          }}
          variant="outlined"
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Email />
              Inboxes
            </Typography>
            <IconButton onClick={() => refreshInboxes()} size="small">
              <RefreshOutlined />
            </IconButton>
          </Box>

          {/* Inbox search bar */}
          <Box sx={{ px: 1, py: 1, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              placeholder="Search inboxes..."
              value={inboxSearchQuery}
              onChange={(e) => setInboxSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>

          {refreshing && <LinearProgress />}

          <List sx={{ flexGrow: 1, overflow: "auto", p: 0 }}>
            {/* All Inboxes option */}
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedInbox === null}
                onClick={() => setSelectedInbox(null)}
                sx={{
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor:
                    selectedInbox === null ? "action.selected" : "transparent",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AllInboxIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      All Inboxes
                    </Typography>
                  }
                  secondary={`${total} inbox${
                    total !== 1 ? "es" : ""
                  }`}
                />
              </ListItemButton>
            </ListItem>

            {inboxes.map((inbox: any) => (
              <ListItem key={inbox.id} disablePadding>
                <ListItemButton
                  selected={selectedInbox?.id === inbox.id}
                  onClick={() => handleSelectInbox(inbox)}
                  sx={{
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={inbox.name}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {inbox.domain}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <Chip
                            label={inbox.sharing === "Shared" ? "Shared" : "Private"}
                            size="small"
                            color={inbox.sharing === "Shared" ? "primary" : "default"}
                            sx={{
                              height: 20,
                              fontSize: "0.75rem",
                            }}
                          />
                          {getInboxTypeIcon(inbox.inboxType) && (
                            <Chip
                              icon={getInboxTypeIcon(inbox.inboxType) as React.ReactElement}
                              label={getInboxTypeLabel(inbox.inboxType)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: "0.75rem",
                                "& .MuiChip-icon": {
                                  marginLeft: "4px",
                                  marginRight: "-4px",
                                },
                              }}
                            />
                          )}
                          {inbox.syncEnabled === false && (
                            <Tooltip title="Email sync is disabled for this inbox">
                              <Chip
                                icon={<SyncDisabled sx={{ fontSize: 14 }} />}
                                label="No Sync"
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{
                                  height: 20,
                                  fontSize: "0.75rem",
                                  "& .MuiChip-icon": {
                                    marginLeft: "4px",
                                    marginRight: "-4px",
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {inbox.ownerEmail && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%"
                            }}
                          >
                            Owner: {inbox.isOwner ? "You" : inbox.ownerEmail}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{
                      minWidth: 0,
                      "& .MuiListItemText-primary": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  <IconButton
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      setSelectedId(inbox.id);
                    }}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}

            {/* No results message */}
            {debouncedInboxSearchQuery && inboxes.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No inboxes match "{debouncedInboxSearchQuery}"
                    </Typography>
                  }
                />
              </ListItem>
            )}

          </List>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleInboxPageChange}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 40,
                px: 1,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
            }}
          />

          <Menu
            anchorEl={anchorEl}
            open={
              Boolean(anchorEl) && inboxes.some((i: any) => i.id === selectedId)
            }
            onClose={() => {
              setAnchorEl(null);
              setSelectedId(null);
            }}
          >
            <MenuItem
              onClick={() => {
                const inboxToEdit: any = inboxes.find(
                  (i: any) => i.id === selectedId
                );
                if (inboxToEdit) {
                  setEditingInbox(inboxToEdit);
                  setEditorOpen(true);
                }
                setAnchorEl(null);
                setSelectedId(null);
              }}
            >
              Edit
            </MenuItem>
            {(() => {
              const inbox = inboxes.find((i: any) => i.id === selectedId);
              return inbox?.isOwner ? (
                <MenuItem
                  onClick={() => {
                    setInboxToTransfer(inbox);
                    setTransferDialogOpen(true);
                    setAnchorEl(null);
                    setSelectedId(null);
                  }}
                >
                  Transfer Ownership
                </MenuItem>
              ) : null;
            })()}
            {isWebhookInbox(selectedId) && (
              <MenuItem
                onClick={() => {
                  setDomainOpen(true);
                  setAnchorEl(null);
                }}
              >
                Webhook Instructions
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                const inboxToDelete: any = inboxes.find(
                  (i: any) => i.id === selectedId
                );
                if (inboxToDelete) {
                  deleteInbox(inboxToDelete.id);
                }
                setAnchorEl(null);
                setSelectedId(null);
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Paper>

        {/* Message Actions Menu */}
        <Menu
          anchorEl={messageMenuAnchor}
          open={Boolean(messageMenuAnchor)}
          onClose={() => {
            setMessageMenuAnchor(null);
            setMessageMenuId(null);
          }}
        >
          <MenuItem
            onClick={() => {
              const message = messages.find((m) => m.id === messageMenuId);
              if (message) {
                onViewMessage(message);
              }
              setMessageMenuAnchor(null);
              setMessageMenuId(null);
            }}
          >
            View
          </MenuItem>
          <MenuItem
            onClick={() => {
              const message = messages.find((m) => m.id === messageMenuId);
              if (message) {
                onReplyMessage(message);
              }
              setMessageMenuAnchor(null);
              setMessageMenuId(null);
            }}
          >
            Reply
          </MenuItem>
          <MenuItem
            onClick={() => {
              const message = messages.find((m) => m.id === messageMenuId);
              setMessageMenuAnchor(null);
              setMessageMenuId(null);
              if (message?.sender) {
                onUnsubscribeSender(message.sender);
              }
            }}
          >
            Add to unsubscribe list
          </MenuItem>
        </Menu>

        {/* Right Panel - Messages */}
        <Paper
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0, // Allow shrinking below content size
            overflow: "hidden",
          }}
          variant="outlined"
        >
          {selectedInbox ? (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>
                  {selectedInbox.name} Messages
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setComposeOpen(true)}
                    size="small"
                  >
                    Compose
                  </Button>
                  {selectedMessages.length > 0 && (
                    <Button
                      startIcon={<DeleteOutline />}
                      onClick={handleBulkDeleteMessages}
                      color="error"
                      size="small"
                    >
                      Delete Selected ({selectedMessages.length})
                    </Button>
                  )}
                  <IconButton onClick={() => refreshMessages()} size="small">
                    <RefreshOutlined />
                  </IconButton>
                </Box>
              </Box>

              {messagesRefreshing && <LinearProgress />}

              {/* Search and Filter */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", gap: 2 }}>
                <FormControl sx={{ width: 200 }} size="small">
                  <InputLabel id="message-type-filter-label">
                    Message Types
                  </InputLabel>
                  <Select
                    labelId="message-type-filter-label"
                    id="message-type-filter"
                    multiple
                    value={selectedMessageTypes}
                    onChange={() => {
                      // Prevent default Select behavior - we handle it in onClick
                    }}
                    input={<OutlinedInput label="Message Types" />}
                    renderValue={(selected) => {
                      const selectedGroups = ALL_MESSAGE_TYPES.filter((group) =>
                        group.types.some((type) => selected.includes(type))
                      );
                      const labels = selectedGroups.map((g) => g.label).join(", ");
                      return (
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {labels}
                        </Box>
                      );
                    }}
                  >
                    {ALL_MESSAGE_TYPES.map((messageTypeGroup, index) => {
                      const isChecked = messageTypeGroup.types.every((type) =>
                        selectedMessageTypes.includes(type)
                      );
                      const isIndeterminate =
                        !isChecked &&
                        messageTypeGroup.types.some((type) =>
                          selectedMessageTypes.includes(type)
                        );

                      return (
                        <MenuItem
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (isChecked) {
                              // Remove all types in this group
                              setSelectedMessageTypes((prev) =>
                                prev.filter(
                                  (t) => !messageTypeGroup.types.includes(t)
                                )
                              );
                            } else {
                              // Add all types in this group
                              setSelectedMessageTypes((prev) => [
                                ...prev.filter(
                                  (t) => !messageTypeGroup.types.includes(t)
                                ),
                                ...messageTypeGroup.types,
                              ]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                          />
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {messageTypeGroup.icon}
                          </ListItemIcon>
                          <ListItemText primary={messageTypeGroup.label} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1 }}
                />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableContainer
                  data-tutorial="inbox-list"
                  sx={{
                    flexGrow: 1,
                    overflowX: "auto",
                    maxWidth: "100%",
                  }}
                  onScroll={handleScroll}
                >
                  <Table
                    stickyHeader
                    sx={{
                      minWidth: { xs: "600px", sm: "700px", md: "100%" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ width: { xs: "40px", md: "auto" } }}
                        >
                          <Checkbox
                            indeterminate={
                              selectedMessages.length > 0 &&
                              selectedMessages.length < messages.length
                            }
                            checked={
                              messages.length > 0 &&
                              selectedMessages.length === messages.length
                            }
                            onChange={handleSelectAllMessages}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "60px", md: "auto" },
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          Type
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "10%" },
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "auto" },
                          }}
                        >
                          From/To
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "40%", md: "auto" },
                          }}
                        >
                          Subject
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "20%" },
                          }}
                        >
                          Content
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "15%" },
                          }}
                        >
                          Classification
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "15%" },
                          }}
                        >
                          Created
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "auto", md: "60px" },
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {messages.map((message) => (
                        <TableRow
                          hover
                          onClick={() => onViewMessage(message)}
                          sx={{ cursor: "pointer" }}
                          tabIndex={-1}
                          key={message.id}
                          selected={isMessageSelected(message.id)}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: { xs: "40px", md: "auto" } }}
                          >
                            <Checkbox
                              checked={isMessageSelected(message.id)}
                              onChange={() => handleSelectMessage(message.id)}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "60px", md: "auto" },
                              display: { xs: "none", sm: "table-cell" },
                            }}
                          >
                            <Tooltip
                              title={getMessageTypeLabel(
                                message.messageType ||
                                  INBOX_MESSAGE_TYPE_RECEIVED
                              )}
                              arrow
                            >
                              <Chip
                                icon={getMessageTypeIcon(
                                  message.messageType ||
                                    INBOX_MESSAGE_TYPE_RECEIVED
                                )}
                                label={getMessageTypeLabel(
                                  message.messageType ||
                                    INBOX_MESSAGE_TYPE_RECEIVED
                                )}
                                color={
                                  getMessageTypeColor(
                                    message.messageType ||
                                      INBOX_MESSAGE_TYPE_RECEIVED
                                  ) as any
                                }
                                size="small"
                                variant={
                                  message.messageType === INBOX_MESSAGE_TYPE_RECEIVED_REPLY
                                    ? "filled"
                                    : "outlined"
                                }
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "10%" },
                            }}
                          >
                            {message.replied ? (
                              <Chip
                                icon={<Reply fontSize="small" />}
                                label="Replied"
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            ) : message.generatedReply ? (
                              <Chip
                                icon={<DraftsOutlined fontSize="small" />}
                                label="Reply Generated"
                                size="small"
                                variant="outlined"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  onReplyMessage(message);
                                }}
                                sx={(theme) => ({
                                  cursor: "pointer",
                                  border: "2px solid transparent",
                                  backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(90deg, #4285f4, #9b72cb, #d96570, #d96570, #9b72cb, #4285f4)`,
                                  backgroundOrigin: "border-box",
                                  backgroundClip: "padding-box, border-box",
                                  borderRadius: "16px",
                                })}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                --
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "auto" },
                            }}
                          >
                            <Box>
                              {/* Show different fields based on message type */}
                              {message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_COMPOSE ||
                              message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_REPLY ||
                              message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_SEQUENCE ? (
                                // For sent messages, show recipient
                                <>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{
                                      display: { xs: "none", sm: "block" },
                                    }}
                                  >
                                    To:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      maxWidth: {
                                        xs: "100px",
                                        sm: "150px",
                                        md: "200px",
                                      },
                                    }}
                                  >
                                    {message.recipient}
                                  </Typography>
                                </>
                              ) : (
                                // For received messages, show sender
                                <>
                                  {message.senderName && (
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      noWrap
                                      sx={{
                                        maxWidth: {
                                          xs: "100px",
                                          sm: "150px",
                                          md: "200px",
                                        },
                                      }}
                                    >
                                      {message.senderName}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{
                                      maxWidth: {
                                        xs: "100px",
                                        sm: "150px",
                                        md: "200px",
                                      },
                                    }}
                                  >
                                    {message.sender}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "40%", md: "auto" },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: {
                                  xs: "120px",
                                  sm: "180px",
                                  md: "200px",
                                },
                              }}
                            >
                              {message.subject}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "20%" },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: { xs: "100px", md: "200px" } }}
                            >
                              {message.bodyPreview}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "15%" },
                            }}
                          >
                            {message.replyClassification ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Tooltip
                                  title={`Intent: ${
                                    message.replyClassification.intent ||
                                    "Unknown"
                                  }\nKeywords: ${
                                    message.replyClassification.keywords?.join(
                                      ", "
                                    ) || "None"
                                  }`}
                                  arrow
                                >
                                  <Chip
                                    label={formatCategory(
                                      message.replyClassification.category
                                    )}
                                    color={
                                      getCategoryColor(
                                        message.replyClassification.category
                                      ) as any
                                    }
                                    size="small"
                                    icon={<Psychology fontSize="small" />}
                                  />
                                </Tooltip>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip
                                    title={`Sentiment: ${message.replyClassification.sentiment}`}
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {getSentimentIcon(
                                        message.replyClassification.sentiment
                                      )}
                                    </Box>
                                  </Tooltip>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {message.replyClassification.confidence.toFixed(
                                      0
                                    )}
                                    % confident
                                  </Typography>
                                </Box>
                                {message.replyClassification
                                  .needsHumanReview && (
                                  <Tooltip
                                    title="This classification needs human review"
                                    arrow
                                  >
                                    <Chip
                                      label="Review Needed"
                                      color="warning"
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No classification
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "15%" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Typography variant="body2">
                              {message.created_date
                                ? moment(message.created_date).format("MMM D, h:mm A")
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "auto", md: "60px" },
                            }}
                          >
                            <IconButton
                              onClick={(e: any) => {
                                e.stopPropagation();
                                setMessageMenuAnchor(e.currentTarget);
                                setMessageMenuId(message.id);
                              }}
                              size="small"
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Loading indicator for infinite scroll */}
                  {messagesLoading && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <LinearProgress sx={{ width: "50%" }} />
                    </Box>
                  )}

                  {/* End of messages indicator */}
                  {!hasMoreMessages && messages.length > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No more messages
                      </Typography>
                    </Box>
                  )}
                </TableContainer>
              </Box>
            </>
          ) : selectedInbox === null ? (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">All Inboxes Messages</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton onClick={() => refreshMessages()} size="small">
                    <RefreshOutlined />
                  </IconButton>
                </Box>
              </Box>

              {messagesRefreshing && <LinearProgress />}

              {/* Search and Filter */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", gap: 2 }}>
                <FormControl sx={{ width: 200 }} size="small">
                  <InputLabel id="message-type-filter-label">
                    Message Types
                  </InputLabel>
                  <Select
                    labelId="message-type-filter-label"
                    id="message-type-filter"
                    multiple
                    value={selectedMessageTypes}
                    onChange={() => {
                      // Prevent default Select behavior - we handle it in onClick
                    }}
                    input={<OutlinedInput label="Message Types" />}
                    renderValue={(selected) => {
                      const selectedGroups = ALL_MESSAGE_TYPES.filter((group) =>
                        group.types.some((type) => selected.includes(type))
                      );
                      const labels = selectedGroups.map((g) => g.label).join(", ");
                      return (
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {labels}
                        </Box>
                      );
                    }}
                  >
                    {ALL_MESSAGE_TYPES.map((messageTypeGroup, index) => {
                      const isChecked = messageTypeGroup.types.every((type) =>
                        selectedMessageTypes.includes(type)
                      );
                      const isIndeterminate =
                        !isChecked &&
                        messageTypeGroup.types.some((type) =>
                          selectedMessageTypes.includes(type)
                        );

                      return (
                        <MenuItem
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (isChecked) {
                              // Remove all types in this group
                              setSelectedMessageTypes((prev) =>
                                prev.filter(
                                  (t) => !messageTypeGroup.types.includes(t)
                                )
                              );
                            } else {
                              // Add all types in this group
                              setSelectedMessageTypes((prev) => [
                                ...prev.filter(
                                  (t) => !messageTypeGroup.types.includes(t)
                                ),
                                ...messageTypeGroup.types,
                              ]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                          />
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {messageTypeGroup.icon}
                          </ListItemIcon>
                          <ListItemText primary={messageTypeGroup.label} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1 }}
                />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableContainer
                  data-tutorial="inbox-list"
                  sx={{
                    flexGrow: 1,
                    overflowX: "auto",
                    maxWidth: "100%",
                  }}
                  onScroll={handleScroll}
                >
                  <Table
                    stickyHeader
                    sx={{
                      minWidth: { xs: "600px", sm: "700px", md: "100%" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            width: { xs: "25%", md: "auto" },
                          }}
                        >
                          Inbox
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "60px", md: "auto" },
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          Type
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "10%" },
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "auto" },
                          }}
                        >
                          From/To
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "40%", md: "auto" },
                          }}
                        >
                          Subject
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "20%" },
                          }}
                        >
                          Content
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "15%" },
                          }}
                        >
                          Classification
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "30%", md: "15%" },
                          }}
                        >
                          Created
                        </TableCell>
                        <TableCell
                          sx={{
                            width: { xs: "auto", md: "60px" },
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {messages.map((message) => (
                        <TableRow
                          hover
                          onClick={() => onViewMessage(message)}
                          sx={{ cursor: "pointer" }}
                          tabIndex={-1}
                          key={message.id}
                        >
                          <TableCell
                            sx={{
                              width: { xs: "25%", md: "auto" },
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                noWrap
                                sx={{ maxWidth: "150px" }}
                              >
                                {message.inbox?.name || "Unknown Inbox"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: "150px" }}
                              >
                                {message.inbox?.domain || "No domain"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "60px", md: "auto" },
                              display: { xs: "none", sm: "table-cell" },
                            }}
                          >
                            <Tooltip
                              title={getMessageTypeLabel(
                                message.messageType ||
                                  INBOX_MESSAGE_TYPE_RECEIVED
                              )}
                              arrow
                            >
                              <Chip
                                icon={getMessageTypeIcon(
                                  message.messageType ||
                                    INBOX_MESSAGE_TYPE_RECEIVED
                                )}
                                label={getMessageTypeLabel(
                                  message.messageType ||
                                    INBOX_MESSAGE_TYPE_RECEIVED
                                )}
                                color={
                                  getMessageTypeColor(
                                    message.messageType ||
                                      INBOX_MESSAGE_TYPE_RECEIVED
                                  ) as any
                                }
                                size="small"
                                variant={
                                  message.messageType === INBOX_MESSAGE_TYPE_RECEIVED_REPLY
                                    ? "filled"
                                    : "outlined"
                                }
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "10%" },
                            }}
                          >
                            {message.replied ? (
                              <Chip
                                icon={<Reply fontSize="small" />}
                                label="Replied"
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            ) : message.generatedReply ? (
                              <Chip
                                icon={<DraftsOutlined fontSize="small" />}
                                label="Reply Generated"
                                size="small"
                                variant="outlined"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  onReplyMessage(message);
                                }}
                                sx={(theme) => ({
                                  cursor: "pointer",
                                  border: "2px solid transparent",
                                  backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(90deg, #4285f4, #9b72cb, #d96570, #d96570, #9b72cb, #4285f4)`,
                                  backgroundOrigin: "border-box",
                                  backgroundClip: "padding-box, border-box",
                                  borderRadius: "16px",
                                })}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                --
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "auto" },
                            }}
                          >
                            <Box>
                              {/* Show different fields based on message type */}
                              {message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_COMPOSE ||
                              message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_REPLY ||
                              message.messageType ===
                                INBOX_MESSAGE_TYPE_SENT_SEQUENCE ? (
                                // For sent messages, show recipient
                                <>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color="text.secondary"
                                    sx={{
                                      display: { xs: "none", sm: "block" },
                                    }}
                                  >
                                    To:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      maxWidth: {
                                        xs: "100px",
                                        sm: "150px",
                                        md: "200px",
                                      },
                                    }}
                                  >
                                    {message.recipient}
                                  </Typography>
                                </>
                              ) : (
                                // For received messages, show sender
                                <>
                                  {message.senderName && (
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      noWrap
                                      sx={{
                                        maxWidth: {
                                          xs: "100px",
                                          sm: "150px",
                                          md: "200px",
                                        },
                                      }}
                                    >
                                      {message.senderName}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{
                                      maxWidth: {
                                        xs: "100px",
                                        sm: "150px",
                                        md: "200px",
                                      },
                                    }}
                                  >
                                    {message.sender}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "40%", md: "auto" },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: {
                                  xs: "120px",
                                  sm: "180px",
                                  md: "200px",
                                },
                              }}
                            >
                              {message.subject}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "20%" },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: { xs: "100px", md: "200px" } }}
                            >
                              {message.bodyPreview}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "15%" },
                            }}
                          >
                            {message.replyClassification ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Tooltip
                                  title={`Intent: ${
                                    message.replyClassification.intent ||
                                    "Unknown"
                                  }\nKeywords: ${
                                    message.replyClassification.keywords?.join(
                                      ", "
                                    ) || "None"
                                  }`}
                                  arrow
                                >
                                  <Chip
                                    label={formatCategory(
                                      message.replyClassification.category
                                    )}
                                    color={
                                      getCategoryColor(
                                        message.replyClassification.category
                                      ) as any
                                    }
                                    size="small"
                                    icon={<Psychology fontSize="small" />}
                                  />
                                </Tooltip>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip
                                    title={`Sentiment: ${message.replyClassification.sentiment}`}
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {getSentimentIcon(
                                        message.replyClassification.sentiment
                                      )}
                                    </Box>
                                  </Tooltip>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {message.replyClassification.confidence.toFixed(
                                      0
                                    )}
                                    % confident
                                  </Typography>
                                </Box>
                                {message.replyClassification
                                  .needsHumanReview && (
                                  <Tooltip
                                    title="This classification needs human review"
                                    arrow
                                  >
                                    <Chip
                                      label="Review Needed"
                                      color="warning"
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No classification
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "30%", md: "15%" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Typography variant="body2">
                              {message.created_date
                                ? moment(message.created_date).format("MMM D, h:mm A")
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: { xs: "auto", md: "60px" },
                            }}
                          >
                            <IconButton
                              onClick={(e: any) => {
                                e.stopPropagation();
                                setMessageMenuAnchor(e.currentTarget);
                                setMessageMenuId(message.id);
                              }}
                              size="small"
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Loading indicator for infinite scroll */}
                  {messagesLoading && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <LinearProgress sx={{ width: "50%" }} />
                    </Box>
                  )}

                  {/* End of messages indicator */}
                  {!hasMoreMessages && messages.length > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No more messages
                      </Typography>
                    </Box>
                  )}
                </TableContainer>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select an inbox to view messages
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      <InboxEditorDialog
        open={editorOpen}
        onClose={() => {
          refreshInboxes();
          setEditorOpen(false);
          setEditingInbox(null); // Clear editing state
        }}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        inbox={editingInbox} // Pass the inbox being edited, not the selected one
      />

      <InboxDomainDialog
        open={domainOpen}
        onClose={() => {
          setDomainOpen(false);
          setSelectedId(null);
        }}
        inbox={inboxes.find((i: any) => i.id === selectedId)}
      />

      <InboxMessagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        message={selectedMessage}
        getToken={getToken}
        companyId={companyId}
        onReply={onReplyMessage}
        onUnsubscribe={onUnsubscribeSender}
      />

      <InboxReplyDialog
        open={replyOpen}
        onClose={() => {
          setReplyOpen(false);
          setReplyMessage(null);
        }}
        onReplySent={() => {
          // Refresh messages to see the reply status update
          refreshMessages(true);
        }}
        message={replyMessage}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        onManageProfiles={() => setSenderProfilesOpen(true)}
      />

      <InboxComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onEmailSent={() => {
          // Refresh messages to see the new email
          refreshMessages(true);
        }}
        selectedInbox={selectedInbox}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        onManageProfiles={() => setSenderProfilesOpen(true)}
      />

      <SenderProfiles
        open={senderProfilesOpen}
        onClose={() => setSenderProfilesOpen(false)}
        companyId={companyId}
        eventListener={eventListener}
        getToken={getToken}
        employees={employees}
        company={company}
      />

      <InboxTypeSelectionDialog
        open={typeSelectionOpen}
        onClose={() => setTypeSelectionOpen(false)}
        onSelectOffice365={() => {
          setTypeSelectionOpen(false);
          setOffice365DialogOpen(true);
        }}
        onSelectImap={() => {
          setTypeSelectionOpen(false);
          setImapDialogOpen(true);
        }}
        onSelectGmail={() => {
          setTypeSelectionOpen(false);
          setGmailDialogOpen(true);
        }}
      />

      <InstallIntegrationDialog
        open={imapDialogOpen}
        onClose={() => setImapDialogOpen(false)}
        selectedIntegration="imap"
        company={company}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        onSaveSuccess={async () => {
          setImapDialogOpen(false);
          // Refresh the inboxes list to show the new IMAP inbox
          await refreshInboxes();
        }}
      />

      <InstallIntegrationDialog
        open={office365DialogOpen}
        onClose={() => setOffice365DialogOpen(false)}
        selectedIntegration="office365"
        company={company}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        onSaveSuccess={async () => {
          setOffice365DialogOpen(false);
          // Refresh the inboxes list to show the new Office 365 inbox
          await refreshInboxes();
        }}
      />

      <InstallIntegrationDialog
        open={gmailDialogOpen}
        onClose={() => setGmailDialogOpen(false)}
        selectedIntegration="gmail"
        company={company}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        onSaveSuccess={async () => {
          setGmailDialogOpen(false);
          // Refresh the inboxes list to show the new Gmail inbox
          await refreshInboxes();
        }}
      />

      <InboxTransferDialog
        open={transferDialogOpen}
        onClose={() => {
          setTransferDialogOpen(false);
          setInboxToTransfer(null);
        }}
        inbox={inboxToTransfer}
        companyId={companyId}
        getToken={getToken}
        onTransferComplete={() => {
          refreshInboxes();
          eventListener({
            type: "SET_SUCCESS",
            message: "Inbox ownership transferred successfully!"
          });
        }}
      />

      <InboxWarmupDialog
        open={warmupDialogOpen}
        onClose={() => setWarmupDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        eventListener={eventListener}
      />

      <InboxImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        eventListener={eventListener}
        onImportComplete={() => {
          refreshInboxes();
        }}
      />

      <ColdOutreachOrderDialog
        open={coldOutreachDialogOpen}
        onClose={() => setColdOutreachDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        onComplete={() => {
          openDialog(
            "Order Placed",
            "Your cold outreach domains and mailboxes have been ordered. They will be ready within 24-48 hours.",
            "OK",
            () => {}
          );
        }}
        employees={employees}
        company={company}
      />

    </Box>
  );
};
